import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { PER_PAGE_DEFAULT, QUERY_NAME, SERVICES } from "@lhd/constants";
import axios from "@lhd/libs/axios";
import { getCompanyId } from "@lhd/utility";
import { notification } from "antd";
// import { ProvisionItem } from "types/provision.type";

export const useSubmitWorkColumn = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ payload }: any) =>
      axios({
        method: "post",
        url: SERVICES.WORK_COLUMNS_SAVE,
        data: payload,
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.COMPANY]);
      },
    },
  );
};

export const useSubmitExpenseType = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ payload }: any) =>
      axios({
        method: "post",
        url: `${SERVICES.EXPENSE_ITEMS}/save`,
        data: payload,
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.EXPENSE_TYPE]);
      },
    },
  );
};

const fetchAccountTitle = async (companyId: any) => {
  return await axios({
    method: "get",
    url: SERVICES.ACCOUNT_TITLES,
    params: {
      page: 1,
      per_page: PER_PAGE_DEFAULT,
      "q[company_id_eq]": companyId,
    },
  }).then((res) => res.data);
};

export const fetchProvision = async (companyId: any, provisionType: number) => {
  return await axios({
    method: "get",
    url: SERVICES.PROVISION_ITEMS,
    params: {
      company_id: companyId,
      page: 1,
      per_page: PER_PAGE_DEFAULT,
      "q[company_id_eq]": companyId,
      "q[provision_type_eq]": provisionType,
    },
  }).then((res) => res.data);
};

export const useFetchAccountTitle = () => {
  const companyId = getCompanyId();

  return useQuery(
    [QUERY_NAME.ACCOUNT_TITLE, companyId],
    () => fetchAccountTitle(companyId),
    {
      cacheTime: Infinity,
      staleTime: Infinity,
      enabled: !!companyId,
    },
  );
};

export const fetchExpenseType = async (companyId: any) => {
  return await axios({
    method: "get",
    url: SERVICES.EXPENSE_ITEMS,
    params: {
      page: 1,
      per_page: PER_PAGE_DEFAULT,
      "q[company_id_eq]": companyId,
    },
  }).then((res) => res.data);
};

export const usePromiseSettledProvisionItem = () => {
  const companyId = getCompanyId();

  return useQueries({
    queries: [
      {
        queryKey: [QUERY_NAME.PROVISION_ITEM, 1, companyId],
        queryFn: () => fetchProvision(companyId, 1),
        staleTime: Infinity,
        cacheTime: Infinity,
        // retry: 1,
      },
      {
        queryKey: [QUERY_NAME.PROVISION_ITEM, 2, companyId],
        queryFn: () => fetchProvision(companyId, 2),
        staleTime: Infinity,
        cacheTime: Infinity,
        // retry: 1,
      },
      {
        queryKey: [QUERY_NAME.PROVISION_ITEM, 3, companyId],
        queryFn: () => fetchProvision(companyId, 3),
        staleTime: Infinity,
        cacheTime: Infinity,
        // retry: 1,
      },
      {
        queryKey: [QUERY_NAME.PROVISION_ITEM, 4, companyId],
        queryFn: () => fetchAccountTitle(companyId),
        staleTime: Infinity,
        cacheTime: Infinity,
        // retry: 1,
      },
      {
        queryKey: [QUERY_NAME.PROVISION_ITEM, 5, companyId],
        queryFn: () => fetchProvision(companyId, 4),
        staleTime: Infinity,
        cacheTime: Infinity,
        // retry: 1,
      },
      {
        queryKey: [QUERY_NAME.PROVISION_ITEM, 6, companyId],
        queryFn: () => fetchProvision(companyId, 5),
        staleTime: Infinity,
        cacheTime: Infinity,
        // retry: 1,
      },
    ],
  });
};
export const useGetPromiseProvisionItem = (provisionType: number) => {
  const companyId = getCompanyId();

  return useQueries({
    queries: [
      {
        queryKey: [QUERY_NAME.PROVISION_ITEM, provisionType, companyId],
        queryFn: () => fetchProvision(companyId, provisionType),
        staleTime: Infinity,
        cacheTime: Infinity,
      },
    ],
  });
};

export const useFetchProvision = <T>() => {
  const companyId = getCompanyId();

  return useQuery<Array<T>>(
    [QUERY_NAME.PROVISION_TYPE, companyId],
    () => fetchProvision(companyId, 1),
    {
      cacheTime: Infinity,
      staleTime: Infinity,
      enabled: !!companyId,
    },
  );
};

export const useFetchExpenseType = <T>() => {
  const companyId = getCompanyId();

  return useQuery<Array<T>>(
    [QUERY_NAME.EXPENSE_TYPE, companyId],
    () => fetchExpenseType(companyId),
    {
      cacheTime: Infinity,
      staleTime: Infinity,
      enabled: !!companyId,
    },
  );
};

export const useFetchUnitProvision = <T>() => {
  const companyId = getCompanyId();

  return useQuery<Array<T>>(
    [QUERY_NAME.PROVISION_ITEM, 5, companyId],
    () => fetchProvision(companyId, 4),
    {
      cacheTime: Infinity,
      staleTime: Infinity,
      enabled: !!companyId,
    },
  );
};

export const useFetchArrangement = () => {
  const companyId = getCompanyId();

  return useQuery(
    [QUERY_NAME.ARRANGEMENT_TYPE, companyId],
    () => fetchProvision(companyId, 2),
    {
      cacheTime: Infinity,
      staleTime: Infinity,
      enabled: !!companyId,
    },
  );
};

export const useFetchAllowance = () => {
  const companyId = getCompanyId();

  return useQuery(
    [QUERY_NAME.ALLOWANCE_TYPE, companyId],
    () => fetchProvision(companyId, 3),
    {
      cacheTime: Infinity,
      staleTime: Infinity,
      enabled: !!companyId,
    },
  );
};

export const useSubmitProvisionItem = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ payload }: any) =>
      axios({
        method: "post",
        url: SERVICES.PROVISION_ITEMS_SAVE,
        data: payload,
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.PROVISION_ITEM]);
        queryClient.invalidateQueries([QUERY_NAME.ACCOUNT_TITLE]);
        notification["success"]({
          message: "保存しました",
        });
      },
    },
  );
};
